/**
 * Global shims for legacy code.
 *
 * Imports vendor libraries from npm and assigns them to window globals.
 * This module must be imported BEFORE any legacy code that depends on these globals.
 *
 * Backbone uses `window.jQuery` through the `Backbone.$ = window.jQuery`
 * assignment below. The npm `backbone` module's own `require("jquery")` does not
 * decide it.
 */

import "./_capture_host_jquery.js";
import "./namespaces.js";
import "./jquery-global.js";
import "./_restore_host_jquery.js";
import select2 from "select2";
// select2's CJS export is a factory that attaches the plugin to the jQuery
// it is given, so it is invoked against `window.jQuery`.
if (typeof select2 === "function") {
  select2(window, window.jQuery);
}
import { reportBootError } from "./boot-error-report.js";
import Backbone from "backbone";
import backboneWithRelational from "backbone-relational";
import underscore from "underscore";
import Handlebars from "handlebars";
import moment from "moment";
import Quill from "quill";
// Eagerly load the tippy/popper tooltip framework. Legacy code calls
// `tippy(element, ...)` as a bare global from many places (Sparkle.Tooltips,
// admin tippy tooltips, etc.). The frameworks module has bare npm imports, so it
// is imported here.
import "#legacy/shared/components/tooltips/frameworks.js";
// Registers the translated moment locales and sets moment's global locale.
import "#legacy/moment_with_locales.js";

// The npm `backbone` module resolves its own `jquery` at module-load time, so
// `Backbone.$` is pointed at `window.jQuery`, where plugins like uberSelect attach.
Backbone.$ = window.jQuery;

// Backbone expects jQuery and underscore on window
window.Backbone = Backbone;
window._ = underscore;

// backbone-relational's pre-bundled UMD factory uses its own copy of Backbone
// (resolved via require('backbone') inside the CJS wrapper). It adds
// RelationalModel, HasOne, HasMany, Relational, and Semaphore to THAT copy,
// not to our `Backbone` import. Copy those properties onto our Backbone so
// legacy code (e.g. appointment_booking/wizard models) can extend
// Backbone.RelationalModel.
if (!Backbone.RelationalModel && backboneWithRelational) {
  const src = backboneWithRelational.default || backboneWithRelational;
  for (const key of ["RelationalModel", "HasOne", "HasMany", "Relational", "Semaphore"]) {
    if (src[key]) {
      Backbone[key] = src[key];
    }
  }
}
// backbone-relational resolves string-based relatedModel references (e.g.
// 'AppointmentBooking.Wizard.Models.VisitTypeRuleSelection') by searching
// its _modelScopes array. Under Vite's pre-bundling the default scope is
// the CJS `exports` object (from backbone-relational's own bundled backbone
// copy), not `window`. Register `window` so the string lookups find
// classes attached to window.AppointmentBooking.*.
if (Backbone.Relational?.store) {
  Backbone.Relational.store.addModelScope(window);
}
window.Handlebars = Handlebars;
window.moment = moment;
window.Quill = Quill;

// Initialize template registries used by legacy code
window.Sparkle.HandlebarsTemplates ??= {};
window.JST ??= {};

// Embed entrypoints have no layout to inline the queue, so it is defined here.
import "./ready-queue.js";
window.Sparkle.reportBootError = reportBootError;
